import React, { Fragment, useState } from 'react';
import i18n from '../../../../i18n';
import { useSelector } from 'react-redux';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { formatISOShortDate } from 'core/date/date';

import {
    subscriptionCurrentSelector,
    subscriptionPaymentStatusSelector,
    subscriptionPlansSelector,
    subscriptionRateDialogTypeSelector,
} from 'state/subscription/selectors';
import { SubscriptionPaymentStatus } from 'state/subscription/reducer';

import { CircularProgressPage } from 'components/CircularProgressPage/CircularProgressPage';
import { Currency } from 'components/Currency/Currency';
import { Text, TextTypography } from 'components/Text/Text';
import { VendorSubscriptionActive } from './VendorSubscriptionActive';
import { VendorSubscriptionInactive } from './VendorSubscriptionInactive';
import { VendorSubscriptionNewCard } from './VendorSubscriptionNewCard';
import { VendorSubscriptionFailure } from './VendorSubscriptionFailure';

import { ShopTypeName } from 'types';
import {
    makeSubscriptionChangePaymentStatusAction,
    makeSubscriptionChangeRateDialogAction,
    makeSubscriptionPauseAction,
} from 'state/subscription/actions';
import { useAction } from 'core/store/store';
import { VendorSubscriptionDialog } from './VendorSubscriptionDialog';
import { DialogNew } from 'components/Dialog/DialogNew';
import { VendorSubscriptionRate } from './VendorSubscriptionRate';
import { LocalStorageKey, removeLocalStorageItem } from '../../../core/storage/storage';
import { authIsoCodeSelector } from '../../../state/auth/selectors';
import { countryPriceByMonth } from '../../../core/utils/utils';

const useStyles = makeStyles(theme => ({
    profile: {
        maxWidth: 800,
        height: '100%',
        margin: 'auto',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up(640)]: {
            marginTop: 24,
            boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
            borderRadius: 20,
        },
        [theme.breakpoints.up(1919)]: {
            maxWidth: 1280,
        },
    },
    rate: {
        maxWidth: 800,
        margin: 'auto',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up(640)]: {
            marginTop: 24,
            boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
            borderRadius: 20,
        },
        [theme.breakpoints.up(1919)]: {
            maxWidth: 1280,
        },
    },
    header: {
        display: 'flex',
        padding: theme.spacing(2.5),
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
    },
    title: {
        paddingRight: theme.spacing(3),
        width: '100%',
    },
    contents: {
        flex: 1,
        borderBottom: `1px solid ${theme.palette.brandPrimary.accent}`,
        [theme.breakpoints.up(640)]: {
            borderBottom: 'none',
        },
    },
    main: {
        borderTop: '1px solid #ddd',
    },
    isOpenSwitch: {
        padding: theme.spacing(2.5),
    },
    headerRow: {
        background: theme.palette.brandPrimary.lightest,
    },
    tableRow: {
        '&:nth-child(2n)': {
            background: theme.palette.brandPrimary.lightest,
        },
    },
    headerCell: {
        border: `1px solid ${theme.palette.brandPrimary.lightest}`,
        '&:first-child': {
            borderLeft: 'none',
        },
        '&:last-child': {
            borderRight: 'none',
        },
    },
    rateCard: {
        backgroundColor: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        boxShadow: '0px 0px 50px rgba(15, 24, 37, 0.05)',
        borderRadius: 20,
        margin: 24,
    },
}));

export const VendorSubscription = () => {
    const { t } = i18n.useTranslation();
    const action = useAction();
    const classes = useStyles();
    const currentSubscription = useSelector(subscriptionCurrentSelector);
    const paymentStatus = useSelector(subscriptionPaymentStatusSelector);
    const subscriptionPlans = useSelector(subscriptionPlansSelector);

    const isoCode = useSelector(authIsoCodeSelector);

    const nextSubscriptionPlan = subscriptionPlans.find(
        el => el.subscriptionPlanId === currentSubscription?.nextSubscriptionPlanId,
    );
    const isBasic = !currentSubscription?.active;

    const subscriptionHasEnded =
        !currentSubscription || (new Date() > new Date(currentSubscription?.renewalDate) && isBasic);
    const onPause = () => {
        action(makeSubscriptionPauseAction);
        handleCloseBasicDialog();
    };

    const showConnectRateDialog = useSelector(subscriptionRateDialogTypeSelector);
    const handleCloseOnlineShopDialog = () => {
        action(makeSubscriptionChangeRateDialogAction, null);
        action(makeSubscriptionChangePaymentStatusAction, SubscriptionPaymentStatus.NONE);
        removeLocalStorageItem(LocalStorageKey.PAYMENT_INTENT);
    };

    const [showBasicDialog, setShowBasicDialog] = useState(false);
    const handleCloseBasicDialog = () => setShowBasicDialog(false);
    const handleOpenBasicDialog = () => setShowBasicDialog(true);

    return (
        <Box mb={5}>
            {showBasicDialog && (
                <DialogNew
                    onClose={handleCloseBasicDialog}
                    isUIEnabled={true}
                    leftButton={t('vendor:userMenu.subscription.dialog.cancel')}
                    onSubmit={onPause}
                    rightButton={t('vendor:userMenu.subscription.dialog.change')}
                    width={573}
                    title={t('vendor:userMenu.subscription.dialog.title')}
                >
                    <Text typography={TextTypography.BODY}>{t('vendor:userMenu.subscription.dialog.text')}</Text>
                </DialogNew>
            )}
            
            <div className={classes.profile}>
                <div className={classes.header}>
                    <div className={classes.title}>
                        <Text typography={TextTypography.TITLE_ACCENT}>{t('vendor:userMenu.subscription.title')}</Text>
                    </div>
                </div>
                <div className={classes.contents}>
                    <Box m={2} ml={3} mt={3}>
                        <Text typography={TextTypography.LEAD_ACCENT}>
                            {t('vendor:userMenu.subscription.settings')}
                        </Text>
                    </Box>
                    <Box m={1} ml={3}>
                        <Text typography={TextTypography.PARA}>{t('vendor:userMenu.subscription.connectedRate')}</Text>
                        <Text typography={TextTypography.PARA_ACCENT}>
                            {subscriptionHasEnded
                                ? t('vendor:userMenu.subscription.basicRate')
                                : currentSubscription?.subscriptionPlan?.name === ShopTypeName.ONLINE_SHOP
                                ? t('vendor:userMenu.subscription.onlineShop')
                                : t('vendor:userMenu.subscription.priority')}
                        </Text>
                    </Box>
                    {currentSubscription && (
                        <>
                            {/*<Box m={1} ml={3}>
                                <Text typography={TextTypography.PARA}>
                                    {t('vendor:userMenu.subscription.ratePrice')}
                                </Text>
                                <Text typography={TextTypography.PARA_ACCENT}>
                                    <Currency suffix={countryPriceByMonth(isoCode)}>
                                        {subscriptionHasEnded ? 0 : currentSubscription.subscriptionPlan?.price}
                                    </Currency>
                                </Text>
                            </Box>*/}
                            {!subscriptionHasEnded && (
                                <Box m={1} ml={3}>
                                    <Text typography={TextTypography.PARA}>
                                        {t('vendor:userMenu.subscription.endDate')}
                                    </Text>
                                    <Text typography={TextTypography.PARA_ACCENT}>
                                        {formatISOShortDate(currentSubscription.renewalDate)}
                                    </Text>
                                </Box>
                            )}
                            {(isBasic ||
                                (nextSubscriptionPlan &&
                                    nextSubscriptionPlan?.subscriptionPlanId !=
                                        currentSubscription?.subscriptionPlan?.subscriptionPlanId)) && (
                                <Box m={1} ml={3}>
                                    <Text typography={TextTypography.PARA}>
                                        {t('vendor:userMenu.subscription.nextRate', {
                                            date: formatISOShortDate(currentSubscription.renewalDate),
                                        })}
                                    </Text>
                                    <Text typography={TextTypography.PARA_ACCENT}>
                                        {isBasic
                                            ? t('vendor:userMenu.subscription.basicRate')
                                            : nextSubscriptionPlan?.name === ShopTypeName.ONLINE_SHOP
                                            ? t('vendor:userMenu.subscription.onlineShop')
                                            : t('vendor:userMenu.subscription.priority')}
                                    </Text>
                                </Box>
                            )}
                        </>
                    )}
                    <div className={classes.main}>
                        {!showConnectRateDialog && (
                            <>
                                {paymentStatus === SubscriptionPaymentStatus.FORM && <VendorSubscriptionNewCard />}
                                {paymentStatus === SubscriptionPaymentStatus.PENDING && <CircularProgressPage />}
                                {paymentStatus === SubscriptionPaymentStatus.FAILURE && <VendorSubscriptionFailure />}
                            </>
                        )}
                        {showConnectRateDialog && (
                            <VendorSubscriptionDialog
                                isoCode={isoCode}
                                rate={showConnectRateDialog}
                                onClose={handleCloseOnlineShopDialog}
                            />
                        )}
                        {paymentStatus === SubscriptionPaymentStatus.NONE && !!currentSubscription && (
                            <Fragment>
                                {currentSubscription.active ? (
                                    <VendorSubscriptionActive />
                                ) : (
                                    <VendorSubscriptionInactive />
                                )}
                            </Fragment>
                        )}
                    </div>
                </div>
            </div>
            <VendorSubscriptionRate handleOpenBasicDialog={handleOpenBasicDialog} />
        </Box>
    );
};
